<script>
	// Components
	import Modal from '@/Components/Modal.svelte';
	import LoadingButton from '@/Components/LoadingButton.svelte';
	// Constants
	import { Products } from '@/Constants/RaceProducts';
	import { EventStatus } from '@/Constants/Event';
	import { BetType, BetTypeNames } from '@/Constants/Bet';
	// Stores
	import MultiLegEventStore from '@/Stores/Racing/MultiLegEventStore';
	// Services
	import { notify } from '@/Services/Notify';

	export let open = false;
	export let meeting = undefined;

	let sending = false;
	let quaddieResults = [
		{
			result_string: '',
			selection_ids: [],
			dividends: [
				{
					product_id: Products.NSW,
					bet_type_id: BetType.EarlyQuadrella,
					value: null,
					prize_pool: null
				},
				{
					product_id: Products.SUP,
					bet_type_id: BetType.EarlyQuadrella,
					value: null,
					prize_pool: null
				},
				{
					product_id: Products.TATTS,
					bet_type_id: BetType.EarlyQuadrella,
					value: null,
					prize_pool: null
				}
			]
		}
	];

	$: quaddieResponse = $MultiLegEventStore;
	$: open && quaddieResponse && calculateResults();

	function calculateResults() {
		quaddieResponse.each((multiLegRace) => {
			multiLegRace.races.data.each((race) => {
				if (race.status.id === EventStatus.Abandoned) {
					// Add X/F and continue
					addRaceResultSet({
						winnerNumber: 'X',
						winnerSelectionId: null
					});
				} else {
					let winnersResultSet = [];
					race.winners.data.each((winners) => {
						if (winners.position === 1) {
							winnersResultSet.push({
								winnerNumber: winners.number,
								winnerSelectionId: winners.id
							});
						}
					});
					addRaceResultSet(winnersResultSet.length === 1 ? winnersResultSet[0] : winnersResultSet);
				}
			});
		});
	}

	function addRaceResultSet(resultsSet) {
		if (Array.isArray(resultsSet)) {
			let newResultSet = [];
			for (const winnerResults of resultsSet) {
				let clonedCurrentResults = JSON.parse(JSON.stringify(quaddieResults));
				for (const raceResultSet in clonedCurrentResults) {
					if (clonedCurrentResults[raceResultSet].result_string !== '') {
						clonedCurrentResults[raceResultSet].result_string = clonedCurrentResults[raceResultSet].result_string + ',';
					}
					clonedCurrentResults[raceResultSet].result_string =
						clonedCurrentResults[raceResultSet].result_string + winnerResults.winnerNumber;
					if (winnerResults.winnerSelectionId !== null) {
						clonedCurrentResults[raceResultSet].selection_ids.push(winnerResults.winnerSelectionId);
					}
				}
				for (const results of clonedCurrentResults) {
					newResultSet.push(results);
				}
			}
			quaddieResults = newResultSet;
		} else {
			for (let raceResultSet in quaddieResults) {
				if (quaddieResults[raceResultSet].result_string !== '') {
					quaddieResults[raceResultSet].result_string = quaddieResults[raceResultSet].result_string + ',';
				}
				quaddieResults[raceResultSet].result_string =
					quaddieResults[raceResultSet].result_string + resultsSet.winnerNumber;
				if (resultsSet.winnerSelectionId !== null) {
					quaddieResults[raceResultSet].selection_ids.push(resultsSet.winnerSelectionId);
				}
			}
		}
	}

	function saveEarlyQuadrellaResults() {
		if (!checkFormIsComplete()) {
			notify({
				type: 'danger',
				title: 'Error!',
				message: 'All fields need to be completed before you can save the results',
				timeout: 3
			});
			return false;
		}
		sending = true;
		MultiLegEventStore.saveManualResults(meeting.id, { results: preparePayload() })
			.then(() => {
				notify({
					type: 'success',
					title: 'Early Quaddie manual results saved',
					message: 'Early Quaddie manual results successfully saved!',
					timeout: 3
				});
				open = false;
			})
			.catch(() => {
				notify({
					type: 'danger',
					title: 'Error!',
					message: 'Problem saving Early Quaddie manual results',
					timeout: 3
				});
			})
			.finally(() => (sending = false));
	}

	function checkFormIsComplete() {
		for (const quaddieResult of quaddieResults) {
			for (const dividendIndex in quaddieResult.dividends) {
				if (
					(parseFloat(quaddieResult.dividends[dividendIndex].value) &&
						parseFloat(quaddieResult.dividends[dividendIndex].value) < 0) ||
					(parseFloat(quaddieResult.dividends[dividendIndex].prize_pool) &&
						parseFloat(quaddieResult.dividends[dividendIndex].prize_pool) < 0)
				) {
					return false;
				}
			}
		}
		return true;
	}

	function preparePayload() {
		let clonedResults = JSON.parse(JSON.stringify(quaddieResults));
		for (let clonedResultIndex in clonedResults) {
			delete clonedResults[clonedResultIndex].result_string;
		}
		return clonedResults;
	}
</script>

{#if open && meeting}
	<Modal on:close={() => (open = false)}>
		<div class="pt-2 font-semibold text-center uppercase" slot="header">Manual result - Early Quaddie</div>
		<div class="flex flex-col w-full px-6 mt-8 space-x-2">
			<div class="grid grid-cols-7 h-full gap-2">
				<!-- HEADER -->
				<div class="font-semibold text-center w-full">{BetTypeNames[BetType.EarlyQuadrella]}</div>
				<div class="col-span-2 font-semibold text-center">&#x250C NSW &#x2510</div>
				<div class="col-span-2 font-semibold text-center">&#x250C SUP &#x2510</div>
				<div class="col-span-2 font-semibold text-center">&#x250C TATTS &#x2510</div>
				{#each quaddieResults as quaddieResult}
					<!-- EXOTIC TYPE -->
					<div class="col-start-1 relative w-full">
						<input
							type="text"
							class="px-1 text-xs text-center form form-input focus:outline-none"
							readonly
							bind:value={quaddieResult.result_string}
						/>
						<span class="absolute top-0 w-full text-center bg-gray-200 rounded-t text-tiny"> Results </span>
					</div>
					{#each quaddieResult.dividends as dividend}
						<!-- DIV/POOL -->
						<div class="relative">
							<input
								type="number"
								class="px-1 text-xs text-center form form-input focus:outline-none"
								value={dividend.value / 100 || null}
								step="0.01"
								required
								on:change={(e) => (dividend.value = Math.round(parseFloat(e.target.value).toFixed(2) * 100))}
							/>
							<span class="absolute top-0 w-full text-center bg-gray-200 rounded-t text-tiny"> DIV </span>
						</div>
						<div class="relative">
							<input
								type="number"
								class="px-1 text-xs text-center form form-input focus:outline-none"
								value={dividend.prize_pool / 100 || null}
								step="0.01"
								required
								on:change={(e) => (dividend.prize_pool = Math.round(parseFloat(e.target.value).toFixed(2) * 100))}
							/>
							<span class="absolute top-0 w-full text-center bg-gray-200 rounded-t text-tiny"> POOL </span>
						</div>
					{/each}
				{/each}
			</div>
			<LoadingButton on:click={() => saveEarlyQuadrellaResults()} loading={sending} className="my-4 btn btn-primary">
				{sending ? 'Saving' : 'Save'}
			</LoadingButton>
			<button class="mb-4 btn btn-muted" on:click={() => (open = false)}>Cancel</button>
		</div>
	</Modal>
{/if}
